import * as d3 from 'd3';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';

import _ from 'lodash';
import { twMerge } from 'tailwind-merge';
import { HoldingResponse, SecurityResponse } from '../../../../server/response/responseScheme';
import { apiClient } from '../../../utils/apiClient';
import { useMobxInstance } from '../../../utils/mobxUtil';
import stringUtil from '../../../utils/stringUtil';
import TopHoldingStickyLabel from './TopHoldingStickyLabel';

class ViewStore {
    readonly institutionSlug: string | undefined;

    maxWeightSum: number = 0;
    holdingGroups: {
        reportYear: number;
        reportQuarter: number;
        weightSum: number;
        holdings: HoldingResponse[];
    }[] = [];
    focusedSecurity: SecurityResponse | undefined = undefined;

    constructor(institutionSlug: string | undefined) {
        this.institutionSlug = institutionSlug;

        makeAutoObservable(this);
    }

    async fetch() {
        if (!this.institutionSlug) {
            return;
        }

        const holdings = await apiClient.report.public.getHistoricalTopFiveHoldings.$query({
            institutionSlug: this.institutionSlug,
        });

        runInAction(() => {
            const grouped = _.groupBy(holdings, (holding) => {
                return [holding.reportYear, holding.reportQuarter];
            });

            this.holdingGroups = _.map(grouped, (group) => {
                const reportYear = group[0].reportYear;
                const reportQuarter = group[0].reportQuarter;
                const weightSum = _.sumBy(group, (holding) => Number(holding.weight));

                return {
                    reportYear,
                    reportQuarter,
                    weightSum,
                    holdings: group,
                };
            });

            this.maxWeightSum =
                _.maxBy(this.holdingGroups, (holdingGroup) => holdingGroup.weightSum)?.weightSum ??
                0;

            const firstHolding = _.first(this.holdingGroups)?.holdings?.[0];
            if (firstHolding) {
                this.setFocusedSecurity(firstHolding.security);
            }
        });
    }

    setFocusedSecurity(security: SecurityResponse | undefined) {
        this.focusedSecurity = security;
    }
}

const HistoricalFiveHoldings = observer(({ institutionSlug }: { institutionSlug: string }) => {
    const viewStore = useMobxInstance(ViewStore, institutionSlug);

    useEffect(() => {
        viewStore.fetch();
    }, [institutionSlug]);

    const totalWeightScale = useMemo(() => {
        return d3.scaleLinear().domain([0, viewStore.maxWeightSum]).range([0, 100]);
    }, [viewStore.maxWeightSum]);

    return (
        <div className="flex flex-col gap-3">
            <TopHoldingStickyLabel
                security={viewStore.focusedSecurity}
                institutionSlug={institutionSlug}
            />
            {_.map(viewStore.holdingGroups, (holdingGroup) => {
                const focusedHolding = _.find(
                    holdingGroup.holdings,
                    (holding) => holding.security.hash === viewStore.focusedSecurity?.hash,
                );

                return (
                    <div
                        key={`${holdingGroup.reportYear}-${holdingGroup.reportQuarter}`}
                        className="flex flex-col gap-1"
                    >
                        <div className="text-base-content/60 flex items-center justify-between text-xs">
                            <div className="grow">
                                <span className="tabular-nums">
                                    {holdingGroup.reportYear}.{holdingGroup.reportQuarter}Q
                                </span>
                                {focusedHolding && (
                                    <span className="text-primary ml-4 text-xs font-bold">
                                        {stringUtil.toPercentage(focusedHolding.weight, 1)}
                                    </span>
                                )}
                            </div>
                            <div className="text-base-content/50 flex items-center text-right text-xs">
                                {stringUtil.toPercentage(holdingGroup.weightSum, 1)}
                            </div>
                        </div>
                        <div className="flex h-8 w-full items-center justify-start">
                            <div
                                className="flex h-8 w-full items-center justify-between gap-1 overflow-hidden rounded-xs"
                                style={{
                                    width: `${totalWeightScale(holdingGroup.weightSum)}%`,
                                }}
                            >
                                {_.map(holdingGroup.holdings, (holding) => {
                                    return (
                                        <div
                                            key={holding.hash}
                                            className={twMerge(
                                                'bg-base-300 text-base-content/70 flex h-full grow cursor-pointer flex-nowrap items-center justify-start rounded-xs px-1',
                                                focusedHolding?.hash === holding.hash &&
                                                    'bg-primary/80 text-primary-content',
                                            )}
                                            style={{
                                                width: stringUtil.toPercentage(holding.weight),
                                            }}
                                            onClick={() => {
                                                viewStore.setFocusedSecurity(holding.security);
                                            }}
                                        >
                                            <div className="overflow-hidden text-left text-xs text-clip whitespace-nowrap">
                                                {holding.security.stock?.ticker ||
                                                    holding.security.name}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

export default HistoricalFiveHoldings;
